<template>
  <div>
    <areYouSureBox
      has-modal-card
      :active="showAreYouSure"
      @response="areYouSureResponseHandler"
    />
    <div class="container columns">
      <form class="column is-two-fifths">
        <b-field :label="t('SectionId')">
          <b-input :value="sectionIdValue" disabled />
        </b-field>
        <b-field :label="t('SectionName')">
          <b-input
            required
            expanded
            v-model="selectedSection.sectionName"
            :placeholder="t('SectionNameInput')"
            :validation-message="
              t('FieldIsRequired', { fieldName: t('SectionName') })
            "
            :disabled="canEditShop === false"
            data-test-id="sectionNameInput"
            ref="sectionNameRef"
          />
        </b-field>
        <b-field :label="t('SectionExternalId')">
          <b-input
            required
            expanded
            v-model="selectedSection.sectionExternalId"
            :placeholder="t('SectionExternalIdInput')"
            :validation-message="
              t('FieldIsRequired', { fieldName: t('SectionExternalId') })
            "
            :disabled="canEditShop === false"
            data-test-id="sectionExternalIdInput"
            ref="sectionExternalIdRef"
          />
        </b-field>
        <b-field grouped position="is-right">
          <b-field>
            <b-button @click="cancelUpdate">
              {{ canEditShop ? t("Cancel") : t("Return") }}
            </b-button>
          </b-field>
          <b-field v-if="canEditShop">
            <b-button
              @click="startProcessing"
              :disabled="isProcessing"
              data-test-id="sectionDetailsSubmit"
            >
              {{ t("Submit") }}
            </b-button>
          </b-field>
        </b-field>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from "@sentry/browser"
import areYouSureBox from "@/components/AreYouSureBox.vue"
import shopsService from "@/services/shopsService"
import { useAppStore } from "@/stores/app"
import { useNotifications } from '@/composables/useNotifications'

// Define props with TypeScript support
const props = defineProps({
  shoppingCenterGroupId: [String, Number],
  canEditShop: {
    type: Boolean,
    default: true
  },
  id: [String, Number]
})

// Define emits
const emit = defineEmits([
  'section-create-completed',
  'section-update-completed', 
  'section-create-canceled',
  'section-update-canceled'
])

const { t } = useI18n()
const store = useAppStore()
const { openNotification: openBuefyNotification } = useNotifications()

// Reactive data with TypeScript typing
const selectedSection = ref({
  sectionId: 0,
  sectionName: '',
  sectionExternalId: '',
  shoppingCenterGroupId: 0
})

const originalSection = ref({
  sectionId: 0,
  sectionName: '',
  sectionExternalId: '',
  shoppingCenterGroupId: 0
})

const isNew = ref(false)
const processing = ref(false)
const showAreYouSure = ref(false)
const sectionUpdated = ref(false)
const sectionNameRef = ref(null)
const sectionExternalIdRef = ref(null)

// Computed properties with proper TypeScript typing
const isProcessing = computed(() => processing.value)
const sectionsList = computed(() => store.getSections)
const sectionIdValue = computed(() => 
  isNew.value ? t("SystemGenerated") : String(selectedSection.value.sectionId || '')
)

// Helper functions with TypeScript typing
const createselectedSection = () => {
  const groupId = typeof props.shoppingCenterGroupId === 'string' 
    ? parseInt(props.shoppingCenterGroupId, 10) 
    : props.shoppingCenterGroupId || 0

  selectedSection.value = {
    sectionId: 0,
    sectionName: "",
    shoppingCenterGroupId: groupId,
    sectionExternalId: "",
  }
  isNew.value = true
}

const startProcessing = () => {
  if (processing.value === false) {
    processing.value = true
    const validationResults = shopsService.validateSectionData([
      sectionExternalIdRef.value,
      sectionNameRef.value,
    ])
    if (validationResults.length > 0) {
      processing.value = false
      const translatedErrors = validationResults
        .map((validator) => t(validator))
        .join(", ")
      openNotification(translatedErrors, "is-danger")
    } else {
      showAreYouSure.value = true
    }
  }
}

const areYouSureResponseHandler = async (action: string) => {
  if (showAreYouSure.value === true) {
    showAreYouSure.value = false
    try {
      if (action === "yes") {
        await sendUpdate()
      }
    } finally {
      processing.value = false
    }
  }
}

const openNotification = (message: string, type: string) => {
  openBuefyNotification({
    message,
    type: type as any
  })
}

const sendUpdate = async () => {
  try {
    // Delegate API call and store update to the service
    await shopsService.handleSectionUpdate(selectedSection.value, isNew.value)
    
    sectionUpdated.value = true
    openNotification(t("SuccessSectionUpdated"), "is-success")
    sectionUpdateComplete()
    return "done"
  } catch (error) {
    console.error('Section update error:', error)
    openNotification(t("ErrorContactUs"), "is-danger")
    Sentry.captureException(error)
    return "done"
  }
}

const sectionUpdateComplete = () => {
  if (selectedSection.value.sectionId && selectedSection.value.sectionName) {
    if (isNew.value) {
      emit("section-create-completed", selectedSection.value)
    } else {
      emit("section-update-completed", selectedSection.value)
    }
  }
}

const cancelUpdate = () => {
  if (isNew.value) {
    emit("section-create-canceled")
  } else {
    emit("section-update-canceled")
  }
}

const getSectionToEdit = () => {
  const sections = sectionsList.value
  const sectionId = typeof props.id === 'string' ? parseInt(props.id, 10) : props.id
  
  const foundSection = sections
    ? sections.find((section) => section.sectionId === sectionId)
    : null
    
  if (foundSection) {
    selectedSection.value = {
      sectionId: foundSection.sectionId,
      sectionName: foundSection.sectionName || '',
      sectionExternalId: foundSection.sectionExternalId || '',
      shoppingCenterGroupId: foundSection.shoppingCenterGroupId || 0
    }
  }
  originalSection.value = { ...selectedSection.value }
}

const revertToOriginalSection = () => {
  if (originalSection.value) {
    Object.assign(selectedSection.value, originalSection.value)
  }
}

const resetSection = () => {
  selectedSection.value = {
    sectionId: 0,
    sectionName: '',
    sectionExternalId: '',
    shoppingCenterGroupId: 0
  }
  sectionUpdated.value = false
  isNew.value = false
}

// Lifecycle hooks with TypeScript support
onMounted(async () => {
  if (props.id) {
    if (!sectionsList.value) {
      await shopsService.getSections()
    }
    getSectionToEdit()
  } else {
    createselectedSection()
  }
})

onBeforeUnmount(() => {
  if (!sectionUpdated.value) {
    revertToOriginalSection()
  }
  resetSection()
})
</script>
